import { useState } from "react";
import { toast } from "sonner";
import { Plus, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, errMsg } from "@/lib/api";

// Properties that can be bulk-changed; `opts` gives a dropdown of allowed values, otherwise a text box.
const PROPS = [
  ["status", "Status", () => ["ACTIVE", "BLOCKED"]], ["item_type", "Type", (m) => m?.item_types], ["group", "Group", (m) => m?.groups],
  ["sub_group", "Sub-group", (m, ch) => (ch.group ? m?.group_subs?.[ch.group] : m?.sub_groups)], ["uom", "UOM", (m) => m?.uoms],
  ["gst_percent", "GST %", (m) => m?.gst], ["hsn_sac", "HSN / SAC"], ["price", "List price"], ["make_brand", "Make / brand"],
  ["replaced_by", "Replaced by (item code)"], ["remark", "Remark"], ["serial_tracked", "Serial tracked", () => ["Yes", "No"]],
];

const newRow = () => ({ id: crypto.randomUUID(), prop: "", value: "" });

const Choose = ({ value, onChange, items, placeholder, testid }) => (
  <Select value={value || undefined} onValueChange={onChange}>
    <SelectTrigger className="bg-white" data-testid={testid}><SelectValue placeholder={placeholder} /></SelectTrigger>
    <SelectContent>{items.map(([v, l]) => <SelectItem key={v} value={String(v)}>{l}</SelectItem>)}</SelectContent>
  </Select>
);

function PropRow({ i, row, used, meta, ch, onChange, onRemove }) {
  const p = PROPS.find((x) => x[0] === row.prop);
  const opts = p?.[2] ? p[2](meta, ch) || [] : null;
  const free = PROPS.filter((x) => x[0] === row.prop || !used.includes(x[0]));
  return (
    <div className="grid grid-cols-[1fr_1fr_auto] gap-2 items-center" data-testid={`bulk-row-${i}`}>
      <Choose value={row.prop} onChange={(v) => onChange({ prop: v, value: "" })} items={free.map(([k, l]) => [k, l])} placeholder="Choose property" testid={`bulk-prop-${i}`} />
      {!p ? <div className="text-xs text-slate-400">Pick a property first</div>
        : opts ? <Choose value={row.value} onChange={(v) => onChange({ ...row, value: v })} items={opts.map((o) => [o, o])} placeholder="Choose new value" testid={`bulk-value-${i}`} />
          : <Input value={row.value} onChange={(e) => onChange({ ...row, value: e.target.value })} placeholder="New value" data-testid={`bulk-value-${i}`} />}
      <Button size="icon" variant="ghost" onClick={onRemove} data-testid={`bulk-remove-${i}`}><X className="h-4 w-4" /></Button>
    </div>
  );
}

function Results({ res }) {
  return (
    <div data-testid="bulk-preview">
      <div className="flex gap-2 text-xs mb-2">{Object.entries(res.summary).map(([k, v]) => <span key={k} className="px-2 py-0.5 rounded border uppercase font-semibold" data-testid={`bulk-summary-${k}`}>{k}: {v}</span>)}</div>
      <div className="max-h-60 overflow-auto border border-slate-200 rounded"><table className="w-full text-xs"><tbody>
        {res.results.map((r) => <tr key={r.row} className="border-b border-slate-100"><td className="px-2 py-1 font-mono">{r.name}</td><td className="px-2 py-1 uppercase font-mono">{r.status}</td><td className="px-2 py-1 text-slate-600">{r.message}</td></tr>)}
      </tbody></table></div>
    </div>
  );
}

export function BulkEditDialog({ open, onClose, codes, meta, onDone }) {
  const [rows, setRows] = useState(() => [newRow()]);
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  const ch = Object.fromEntries(rows.filter((r) => r.prop && r.value !== "").map((r) => [r.prop, r.value]));
  const update = (next) => { setRows(next); setRes(null); };
  const run = async (commit) => {
    setBusy(true);
    try {
      const { serial_tracked: st, ...rest } = ch;
      let data = { committed: commit, created: 0, summary: { ok: codes.length }, results: codes.map((c, i) => ({ row: i + 1, name: c, status: "ok", message: `Serial tracked → ${st}` })) };
      if (Object.keys(rest).length) data = (await api.post(`/items/bulk-update?commit=${commit}`, { codes, changes: rest })).data;
      if (st && commit) {
        const ok = Object.keys(rest).length ? data.results.filter((r) => r.status !== "error").map((r) => r.item_code || r.name) : codes;
        if (ok.length) await api.post("/items/serial-tracked", { codes: ok, serial_tracked: st === "Yes" });
        data = { ...data, created: Object.keys(rest).length ? data.created : ok.length };
      }
      setRes(data);
      if (commit) { toast.success(`${data.created} items updated`); onDone(); }
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  const close = () => { setRes(null); setRows([newRow()]); onClose(); };
  return (
    <Dialog open={open} onOpenChange={(v) => !v && close()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="bulk-edit-dialog">
        <DialogHeader><DialogTitle>Bulk change {codes.length} selected item{codes.length === 1 ? "" : "s"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <p className="text-xs text-slate-500">Only the ticked items and only the properties you choose here are changed. Every change is recorded in each item's history.</p>
          <div className="grid grid-cols-[1fr_1fr_auto] gap-2"><Label>Property</Label><Label>New value</Label><span className="w-9" /></div>
          {rows.map((r, i) => <PropRow key={r.id} i={i} row={r} meta={meta} ch={ch} used={rows.map((x) => x.prop)}
            onChange={(v) => update(rows.map((x, j) => (j === i ? { ...v, id: x.id } : x)))} onRemove={() => update(rows.length > 1 ? rows.filter((_, j) => j !== i) : [newRow()])} />)}
          {rows.length < PROPS.length && <Button size="sm" variant="outline" onClick={() => update([...rows, newRow()])} data-testid="bulk-add-prop"><Plus className="h-4 w-4 mr-1" />Add another property</Button>}
          {res && <Results res={res} />}
        </div>
        <DialogFooter>
          {!res?.committed && <Button variant="outline" disabled={busy || !codes.length || !Object.keys(ch).length} onClick={() => run(false)} data-testid="bulk-preview-btn">Preview changes</Button>}
          {res && !res.committed && <Button disabled={busy || !res.summary.ok} onClick={() => run(true)} data-testid="bulk-apply-btn">Apply to {res.summary.ok} items</Button>}
          {res?.committed && <Button onClick={close} data-testid="bulk-close-btn">Done</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
